import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Eye, EyeOff, Scale, FolderOpen, ShieldCheck, Sparkles, Building2, CheckCircle2, ArrowRight, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { signUpWithTransaction } from "@/lib/services/auth";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import emailjs from "@emailjs/browser";
import Logo from "@/components/Logo";
import Turnstile from "@/components/common/Turnstile";
import { isDisposableEmail } from "@/lib/utils/emailValidator";


const formSchema = z.object({
  tenantName: z
    .string()
    .min(1, {
      message: "El nombre del despacho es requerido.",
    })
    .max(50, {
      message: "El nombre del despacho no puede tener más de 50 caracteres.",
    }),
  email: z
    .string()
    .email({
      message: "Por favor ingrese un correo electrónico válido.",
    })
    .max(60, {
      message: "El correo electrónico no puede tener más de 60 caracteres.",
    })
    .refine((val) => !isDisposableEmail(val), {
      message: "No se permiten correos o dominios temporales. Por favor utiliza un correo corporativo o personal válido.",
    }),
  password: z.string().min(8, {
    message: "La contraseña debe tener al menos 8 caracteres.",
  }),
  fullName: z
    .string()
    .min(1, {
      message: "El nombre completo es requerido.",
    })
    .max(30, {
      message: "El nombre completo no puede tener más de 30 caracteres.",
    }),
  cedula: z
    .string()
    .max(15, {
      message: "La cédula no puede tener más de 15 caracteres.",
    })
    .regex(/^\d+$/, {
      message: "La cédula solo puede contener números.",
    })
    .optional()
    .or(z.literal("")),
  phone: z
    .string()
    .max(13, {
      message: "El teléfono no puede tener más de 13 caracteres.",
    })
    .optional()
    .or(z.literal("")),
  referralCode: z.string().max(30).optional().or(z.literal("")), 
  acceptTerms: z.boolean().refine((val) => val === true, {
    message: "Debes aceptar los términos y condiciones para continuar.",
  }),
});

type FormData = z.infer<typeof formSchema>;

export default function Signup() {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isTrial = searchParams.get("trial") === "true";
  const trialPlan = searchParams.get("plan");
  const inviteEmail = searchParams.get("email") || "";
  const inviteTenantName = searchParams.get("tenant_name") || "";
  const inviteTenantId = searchParams.get("tenant_id") || "";
  const isInvited = searchParams.get("invited") === "true";
  const rawRefCode = searchParams.get("ref") || "";
  const rawPromoCode = searchParams.get("promo") || searchParams.get("coupon") || "";
  const isWarRoomPromo =
    rawRefCode?.toUpperCase() === "WARROOM20" ||
    rawPromoCode?.toUpperCase() === "WARROOM20" ||
    (typeof window !== "undefined" && localStorage.getItem("mia_promo_code") === "WARROOM20");

  const [storedRefCode] = useState(() => {
    if (isWarRoomPromo) {
      try {
        localStorage.setItem("mia_referral_code", "WARROOM20");
        localStorage.setItem("mia_promo_code", "WARROOM20");
        localStorage.setItem("mia_discount_percent", "20");
      } catch (_) {}
      return "WARROOM20";
    }
    if (rawRefCode) {
      try { localStorage.setItem("mia_referral_code", rawRefCode); } catch (_) {}
      return rawRefCode;
    }
    try { return localStorage.getItem("mia_referral_code") || ""; } catch (_) { return ""; }
  });
  const [errorMessage, setErrorMessage] = useState("");

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      tenantName: inviteTenantName,
      email: inviteEmail,
      password: "",
      fullName: "",
      cedula: "",
      phone: "",
      referralCode: storedRefCode,
      acceptTerms: false,
    },
  });

  useEffect(() => {
    if (inviteTenantName) {
      form.setValue("tenantName", inviteTenantName);
    }
    if (inviteEmail) {
      form.setValue("email", inviteEmail);
    }
    if (storedRefCode) {
      form.setValue("referralCode", storedRefCode);
    }
  }, [inviteTenantName, inviteEmail, storedRefCode, form]);

  useEffect(() => {
    if (isTrial && trialPlan) {
      toast({
        title: "Prueba Gratuita",
        description: `Estás registrándote para una prueba gratuita de 3 días del plan ${trialPlan}.`,
      });
    }
  }, [isTrial, trialPlan, toast]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    form.reset({ ...form.getValues(), [name]: value });
    if (errorMessage) setErrorMessage("");
  };

  const handleCheckboxChange = (checked: boolean) => {
    form.setValue("acceptTerms", checked);
    if (errorMessage) setErrorMessage("");
  };

  const sendWelcomeEmail = async (email: string, fullName: string) => {
    try {
      const templateParams = {
        to_email: email,
        to_name: fullName,
        subject: "Bienvenido a Mi Asistente Legal IA",
        message: `¡Hola ${fullName}! Gracias por registrarte en Mi Asistente Legal IA. Estamos emocionados de tenerte a bordo.`,
      };

      const result = await emailjs.send(
        "service_8b0q0q8", // Your EmailJS service ID
        "template_ocvcl1q", // Your EmailJS template ID
        templateParams,
        "f50PX44k84hRTGXyy" // EmailJS public key
      );

      if (result.status === 200) {
        return { success: true };
      } else {
        throw new Error("Error al enviar el correo de bienvenida");
      }
    } catch (error) {
      console.error("Error sending welcome email:", error);
      return { success: false, error };
    }
  };

  const onSubmit = async (data: FormData) => {
    try {
      setLoading(true);
      const { error } = await signUpWithTransaction({
        email: data.email,
        password: data.password,
        full_name: data.fullName,
        plan_id: trialPlan || "empresarial",
        is_trial: true,
        phone_number: data.phone,
        cedula: data.cedula,
        referral_code: data.referralCode,
        // For invited users: pass tenant_id directly; skip tenant_name so no new tenant is created.
        tenant_name: isInvited ? undefined : data.tenantName,
        tenant_id: isInvited ? inviteTenantId : undefined,
      });

      if (error) {
        throw new Error(error);
      }

      // Send welcome email
      // const emailResult = await sendWelcomeEmail(data.email, data.fullName);
      // if (!emailResult.success) {
      //   console.warn("Failed to send welcome email:", emailResult.error);
      //   toast({
      //     title: "Error al enviar el correo de bienvenida",
      //     description:
      //       "Tu cuenta se ha creado, pero no pudimos enviar el correo de bienvenida.",
      //     variant: "destructive",
      //   });
      // } else {
      //   toast({
      //     title: "¡Bienvenido!",
      //     description:
      //       "Tu cuenta se ha creado exitosamente y hemos enviado un correo de bienvenida.",
      //     variant: "default",
      //   });
      // }

      navigate("/email-validation");
    } catch (error) {
      console.error("Signup error:", error);

      let errorMessage = "Ocurrió un error inesperado";

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === "string") {
        errorMessage = error;
      } else if (error && typeof error === "object" && "message" in error) {
        errorMessage = (error as any).message;
      }

      // Translate common Supabase Auth errors
      if (errorMessage.includes("Password should contain at least one character of each")) {
        errorMessage = "La contraseña debe contener al menos un carácter de cada tipo: minúscula, mayúscula, número y carácter especial. La contraseña proporcionada es débil o fácil de adivinar, por favor elige una diferente.";
      } else if (errorMessage.includes("User already registered")) {
        errorMessage = "Este correo electrónico ya está registrado.";
      } else if (errorMessage.includes("Password should be at least")) {
        errorMessage = "La contraseña debe tener al menos 8 caracteres.";
      }

      // Set error message for display in the form
      setErrorMessage(errorMessage);

      toast({
        title: "Error al crear la cuenta",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <div className="flex flex-col lg:flex-row min-h-screen">
        {/* Left Section - Features & Capabilities */}
        <div className="hidden lg:flex lg:w-3/5 bg-gradient-to-br from-slate-50 via-blue-50/50 to-indigo-50/60 p-12 xl:p-16 flex-col justify-between relative overflow-hidden border-r border-slate-200/80">
          {/* Ambient lighting */}
          <div className="absolute top-10 right-10 w-96 h-96 bg-blue-300/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-indigo-200/30 rounded-full blur-3xl pointer-events-none" />

          {/* Top Logo */}
          <div className="relative z-10">
            <Link to="/" className="inline-block hover:opacity-90 transition-opacity">
              <Logo className="text-xl" />
            </Link>
          </div>

          {/* Center Content */}
          <div className="max-w-xl my-auto relative z-10 py-6">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-100/80 border border-blue-200 text-blue-800 text-xs font-bold uppercase tracking-wider mb-6 shadow-sm">
              <Sparkles className="w-3.5 h-3.5 text-blue-600" />
              <span>Prueba Gratuita • 3 Días de Acceso Total</span>
            </div>

            <h2 className="text-4xl xl:text-5xl font-black text-slate-900 mb-4 leading-tight tracking-tight">
              Potencia tu despacho con <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">MíA</span>
            </h2>

            <p className="text-slate-600 text-base xl:text-lg mb-8 leading-relaxed font-normal">
              Únete a más de 2,400 litigantes y firmas en Colombia que redactan, vigilan expedientes y recuperan cartera con Inteligencia Artificial especializada.
            </p>

            {/* Features List */}
            <div className="space-y-3.5 mb-8">
              <div className="flex items-start gap-3.5 p-4 rounded-2xl bg-white/90 border border-slate-200/90 shadow-sm backdrop-blur-sm hover:border-blue-200 transition-all">
                <div className="w-9 h-9 rounded-xl bg-blue-50 border border-blue-200 flex items-center justify-center shrink-0 text-blue-600">
                  <Scale className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="font-bold text-sm text-slate-900">Jurisprudencia de Altas Cortes en Vivo</h4>
                  <p className="text-xs text-slate-500">Acceso a más de 1.8M de providencias de la Corte Constitucional, Suprema y Consejo de Estado.</p>
                </div>
              </div>

              <div className="flex items-start gap-3.5 p-4 rounded-2xl bg-white/90 border border-slate-200/90 shadow-sm backdrop-blur-sm hover:border-blue-200 transition-all">
                <div className="w-9 h-9 rounded-xl bg-indigo-50 border border-indigo-200 flex items-center justify-center shrink-0 text-indigo-600">
                  <FolderOpen className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="font-bold text-sm text-slate-900">Control de Expedientes y Términos</h4>
                  <p className="text-xs text-slate-500">Monitoreo continuo de radicados de la Rama Judicial con cálculo automático de términos procesales.</p>
                </div>
              </div>

              <div className="flex items-start gap-3.5 p-4 rounded-2xl bg-white/90 border border-slate-200/90 shadow-sm backdrop-blur-sm hover:border-blue-200 transition-all">
                <div className="w-9 h-9 rounded-xl bg-violet-50 border border-violet-200 flex items-center justify-center shrink-0 text-violet-600">
                  <Building2 className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="font-bold text-sm text-slate-900">Gestión de Cartera en Propiedad Horizontal</h4>
                  <p className="text-xs text-slate-500">Procesos monitorios, cobros y recaudos automatizados bajo Ley 675 y Ley 2300 de 2023.</p>
                </div>
              </div>

              <div className="flex items-start gap-3.5 p-4 rounded-2xl bg-white/90 border border-slate-200/90 shadow-sm backdrop-blur-sm hover:border-blue-200 transition-all">
                <div className="w-9 h-9 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0 text-emerald-600">
                  <ShieldCheck className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="font-bold text-sm text-slate-900">Seguridad Bancaria y Sin Tarjeta</h4>
                  <p className="text-xs text-slate-500">Cifrado AES-256, cumplimiento Ley 1581 Habeas Data y sin cobros automáticos sorpresa.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Footer */}
          <div className="relative z-10 pt-4 border-t border-slate-200/80 flex items-center justify-between text-xs text-slate-500">
            <span>© {new Date().getFullYear()} MíA • Asistente Legal IA en Colombia</span>
            <Link to="/#faq" className="hover:text-blue-600 transition-colors">
              Ayuda & Soporte
            </Link>
          </div>
        </div>

        {/* Right Section - Signup Form */}
        <div className="w-full lg:w-2/5 bg-white dark:bg-slate-900 flex items-center justify-center p-6 sm:p-12 border-l border-slate-200/80 dark:border-slate-800">
          <div className="w-full max-w-lg">
            {/* Mobile Logo */}
            <div className="lg:hidden mb-8">
              <Link to="/">
                <Logo />
              </Link>
            </div>

            <div className="mb-6">
              <h1 className="text-3xl font-black text-slate-900 dark:text-white mb-2 tracking-tight">
                Crear tu Despacho
              </h1>
              <p className="text-slate-500 dark:text-slate-400 text-sm">
                Configura tu acceso profesional en menos de 2 minutos
              </p>
            </div>

            {errorMessage && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-4 rounded-xl mb-6">
                {errorMessage}
              </div>
            )}

            {isWarRoomPromo && (
              <div className="p-4 rounded-2xl bg-gradient-to-r from-amber-500/15 via-blue-500/10 to-indigo-500/15 border border-amber-500/30 text-amber-950 dark:text-amber-200 mb-6 flex items-start gap-3 shadow-sm">
                <Sparkles className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-extrabold text-sm flex items-center gap-2">
                    <span>¡Beneficio Exclusivo War Room Legal Academy!</span>
                    <span className="text-[10px] bg-amber-200 dark:bg-amber-900/60 text-amber-900 dark:text-amber-200 px-2 py-0.5 rounded-full font-black">20% OFF</span>
                  </h4>
                  <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 leading-relaxed">
                    Tu cortesía para el seguimiento de procesos judiciales en la Rama Judicial ha sido activada: <strong>20% de descuento en tu primer mes</strong> con el código <code>WARROOM20</code>.
                  </p>
                </div>
              </div>
            )}

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-5">
                {/* Modern Step Progress */}
                <div className="mb-6 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200/70 dark:border-slate-800">
                  <div className="flex items-center justify-between text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
                    <span>Configuración de cuenta</span>
                    <span className="text-blue-600 dark:text-blue-400 font-extrabold">Paso 1 de 2</span>
                  </div>
                  <div className="h-1.5 w-full bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full transition-all duration-300"
                      style={{
                        width: `${Math.min(
                          (Object.keys(form.getValues())
                            .filter((key) => key !== "acceptTerms" && key !== "cedula" && key !== "phone")
                            .filter((key) => {
                              const value = form.getValues()[key as keyof FormData];
                              return value && value.toString().trim() !== "";
                            }).length /
                            4) *
                          100,
                          100
                        )}%`,
                      }}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="tenantName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                          Nombre del Despacho / Bufete / Razón Social
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Ej: Despacho Jurídico Gómez & Asociados"
                            maxLength={50}
                            className="h-12 bg-slate-50/70 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 rounded-xl focus:bg-white dark:focus:bg-slate-900 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all text-slate-900 dark:text-white placeholder:text-slate-400"
                            disabled={isInvited}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="fullName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                          Nombre Completo del Titular
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Dr(a). Nombre y Apellido"
                            maxLength={30}
                            className="h-12 bg-slate-50/70 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 rounded-xl focus:bg-white dark:focus:bg-slate-900 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all text-slate-900 dark:text-white placeholder:text-slate-400"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                          Correo Electrónico Profesional
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="abogado@tudespacho.com"
                            maxLength={60}
                            className="h-12 bg-slate-50/70 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 rounded-xl focus:bg-white dark:focus:bg-slate-900 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all text-slate-900 dark:text-white placeholder:text-slate-400"
                            disabled={isInvited}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                          Contraseña Segura (mínimo 8 caracteres)
                        </FormLabel>
                        <div className="relative">
                          <FormControl>
                            <Input
                              type={showPassword ? "text" : "password"}
                              placeholder="••••••••••••"
                              className="h-12 bg-slate-50/70 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 rounded-xl pr-12 focus:bg-white dark:focus:bg-slate-900 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all text-slate-900 dark:text-white placeholder:text-slate-400"
                              {...field}
                            />
                          </FormControl>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                            className="absolute right-1 top-1 h-10 w-10 p-0 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
                            onClick={() => setShowPassword(!showPassword)}>
                            {showPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Optional Fields Section */}
                  <details className="group border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden transition-all duration-300">
                    <summary className="flex items-center justify-between p-3.5 cursor-pointer bg-slate-50/60 dark:bg-slate-800/40 hover:bg-slate-100/60 dark:hover:bg-slate-800/70 list-none">
                      <span className="text-xs font-bold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-950 text-blue-600 dark:text-blue-400 flex items-center justify-center text-[10px] font-extrabold">+</span>
                        Información Adicional de Facturación (Opcional)
                      </span>
                      <svg className="w-4 h-4 text-slate-400 group-open:rotate-180 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </summary>
                    <div className="p-4 space-y-4 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="cedula"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                                Cédula o NIT
                              </FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Ej: 1020304050"
                                  maxLength={15}
                                  className="h-11 bg-slate-50/70 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 rounded-xl focus:bg-white dark:focus:bg-slate-900 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all text-slate-900 dark:text-white placeholder:text-slate-400"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="phone"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                                WhatsApp / Móvil
                              </FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="+57 310 123 4567"
                                  maxLength={13}
                                  className="h-11 bg-slate-50/70 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 rounded-xl focus:bg-white dark:focus:bg-slate-900 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all text-slate-900 dark:text-white placeholder:text-slate-400"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </details>

                  {/* Referral Code Field (Optional) */}
                  <FormField
                    control={form.control}
                    name="referralCode"
                    render={({ field }) => (
                      <FormItem className="pt-1">
                        <div className="flex items-center justify-between mb-1">
                          <FormLabel className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                            <Gift className="w-3.5 h-3.5 text-violet-600" />
                            ¿Tienes un código de referido? (Opcional)
                          </FormLabel>
                          {field.value && (
                            <span className="text-[10px] bg-violet-100 text-violet-800 font-bold px-2 py-0.5 rounded-full">
                              Código Detectado
                            </span>
                          )}
                        </div>
                        <FormControl>
                          <Input
                            placeholder="Ej: MIA-4F8A"
                            maxLength={25}
                            className="h-11 bg-slate-50/70 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 rounded-xl uppercase font-mono text-xs focus:bg-white dark:focus:bg-slate-900 focus:border-violet-500 focus:ring-4 focus:ring-violet-500/10 transition-all text-slate-900 dark:text-white placeholder:text-slate-400"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="acceptTerms"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0 pt-2">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={handleCheckboxChange}
                            className="mt-1"
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel className="text-xs text-slate-600 dark:text-slate-400 cursor-pointer">
                            He leído y acepto los{" "}
                            <Link
                              to="/terms"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 dark:text-blue-400 font-semibold hover:underline">
                              Términos y Condiciones
                            </Link>{" "}
                            y la Política de Tratamiento de Datos Personales.
                          </FormLabel>
                          <FormMessage />
                        </div>
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex justify-center my-3">
                  <Turnstile onVerify={() => {}} />
                </div>

                <div className="pt-2">
                  <Button
                    type="submit"
                    className="w-full h-12 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold text-base rounded-xl shadow-lg shadow-blue-500/25 hover:shadow-blue-500/35 transition-all flex items-center justify-center gap-2 group cursor-pointer"
                    disabled={loading}>
                    {loading ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Creando despacho...
                      </>
                    ) : (
                      <>
                        <span>Comenzar Prueba Gratis de 3 Días</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </Form>

            <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-800 text-center">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                ¿Ya tienes una cuenta registrada?{" "}
                <Link
                  to="/login"
                  className="text-blue-600 dark:text-blue-400 hover:text-blue-700 font-bold hover:underline">
                  Iniciar sesión
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
